import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../lib/api';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Separator } from '../ui/separator';
import { CreditCard, Link2, Unlink, Save, KeyRound, CheckCircle2, ExternalLink } from 'lucide-react';

/** Configurazione pagamento carta con lettore fisico SumUp (Solo/Air). */
export const SumUpSettings = () => {
  const { t } = useTranslation();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiKey, setApiKey] = useState('');
  const [merchantCode, setMerchantCode] = useState('');
  const [pairingCode, setPairingCode] = useState('');
  const [readerName, setReaderName] = useState('Cassa');
  const [pairing, setPairing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get('/sumup/config');
      setCfg(data);
      setMerchantCode(data.merchant_code || '');
    } catch (e) {
      // configurazione non ancora presente
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const saveCfg = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = { merchant_code: merchantCode, ...extra };
      if (apiKey.trim()) payload.api_key = apiKey.trim();
      const { data } = await api.put('/sumup/config', payload);
      setCfg(data);
      setApiKey('');
      toast.success(t('sumup.saved'));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('sumup.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = async (v) => {
    await saveCfg({ enabled: v });
  };

  const pairReader = async () => {
    if (!pairingCode.trim()) { toast.error(t('sumup.pairingCodeRequired')); return; }
    setPairing(true);
    try {
      const { data } = await api.post('/sumup/pair', { pairing_code: pairingCode.trim(), name: readerName || 'Cassa' });
      toast.success(t('sumup.paired', { name: data.name || '' }));
      setPairingCode('');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('sumup.pairError'));
    } finally {
      setPairing(false);
    }
  };

  const unpairReader = async () => {
    try {
      await api.delete('/sumup/reader');
      toast.success(t('sumup.unpaired'));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('sumup.saveError'));
    }
  };

  if (loading) return <Card className="p-6"><div className="h-24 animate-pulse bg-muted/40 rounded-xl" /></Card>;

  const hasKey = cfg?.has_api_key;
  const readerPaired = !!cfg?.reader_id;

  return (
    <Card className="p-6" data-testid="sumup-settings">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
            <CreditCard className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-display font-bold text-lg leading-tight">{t('sumup.title')}</h3>
            <p className="text-sm text-muted-foreground">{t('sumup.subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Label htmlFor="sumup-enabled" className="text-sm">{t('sumup.enabled')}</Label>
          <Switch id="sumup-enabled" checked={!!cfg?.enabled} onCheckedChange={toggleEnabled} disabled={!hasKey || !readerPaired} data-testid="sumup-enabled-switch" />
        </div>
      </div>

      {/* Istruzioni credenziali */}
      <div className="mt-4 rounded-xl border border-border bg-muted/30 p-3 text-sm text-muted-foreground space-y-1">
        <p className="font-medium text-foreground">{t('sumup.credentialsHelpTitle')}</p>
        <p>{t('sumup.credentialsHelp1')}</p>
        <p>{t('sumup.credentialsHelp2')}</p>
        <a href="https://me.sumup.com/settings/api-keys" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
          {t('sumup.openSumupDashboard')} <ExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>

      {/* Credenziali API */}
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <Label>{t('sumup.merchantCode')}</Label>
          <Input value={merchantCode} onChange={(e) => setMerchantCode(e.target.value)} placeholder="MXXXXXXX" className="mt-1.5 font-mono" data-testid="sumup-merchant-input" />
        </div>
        <div>
          <Label className="flex items-center gap-1.5"><KeyRound className="h-3.5 w-3.5" /> {t('sumup.apiKey')}</Label>
          <Input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={hasKey ? cfg.api_key_masked : 'sup_sk_...'}
            className="mt-1.5 font-mono"
            data-testid="sumup-apikey-input"
          />
          {hasKey && <p className="text-xs text-muted-foreground mt-1">{t('sumup.apiKeySaved')}</p>}
        </div>
      </div>
      <div className="mt-3">
        <Button onClick={() => saveCfg()} disabled={saving} className="rounded-xl" data-testid="sumup-save-button">
          <Save className="h-4 w-4 mr-2" /> {saving ? t('sumup.saving') : t('sumup.saveCredentials')}
        </Button>
      </div>

      <Separator className="my-6" />

      {/* Lettore fisico */}
      <div>
        <h4 className="font-display font-semibold">{t('sumup.readerTitle')}</h4>
        {readerPaired ? (
          <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-pos-free/30 bg-pos-free/10 p-3">
            <div className="flex items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-pos-free" />
              <span data-testid="sumup-reader-status">{t('sumup.readerConnected', { name: cfg.reader_name || cfg.reader_id })}</span>
            </div>
            <Button variant="outline" size="sm" className="rounded-lg" onClick={unpairReader} data-testid="sumup-unpair-button">
              <Unlink className="h-4 w-4 mr-1.5" /> {t('sumup.unpair')}
            </Button>
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <p className="text-sm text-muted-foreground">{t('sumup.pairHelp')}</p>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-1">
                <Label>{t('sumup.pairingCode')}</Label>
                <Input value={pairingCode} onChange={(e) => setPairingCode(e.target.value.toUpperCase())} placeholder="123456" className="mt-1.5 font-mono" data-testid="sumup-pairing-input" />
              </div>
              <div className="sm:col-span-1">
                <Label>{t('sumup.readerName')}</Label>
                <Input value={readerName} onChange={(e) => setReaderName(e.target.value)} placeholder="Cassa 1" className="mt-1.5" data-testid="sumup-readername-input" />
              </div>
              <div className="sm:col-span-1 flex items-end">
                <Button onClick={pairReader} disabled={pairing || !hasKey} className="rounded-xl w-full" data-testid="sumup-pair-button">
                  <Link2 className="h-4 w-4 mr-2" /> {pairing ? t('sumup.pairing') : t('sumup.pair')}
                </Button>
              </div>
            </div>
            {!hasKey && <p className="text-xs text-destructive">{t('sumup.needKeyFirst')}</p>}
          </div>
        )}
      </div>
    </Card>
  );
};
